import {
  HelicopterIcon,
  MarkerIcon,
  ClockIcon,
  PhoneIcon,
} from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { UnreadNotificationIcon } from '@rsos/components/capstone/UnreadNotificationIcon/UnreadNotificationIcon';
import type { EtlTransportResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { formatDateWithLocalTimezone } from '../../../../../utils/dateUtilities';
import {
  OriginSourcePill,
  OriginSourceTitleRow,
} from '../../../../AirAmbulanceForm/etl/OriginSource.styles';
import { resolveTransportStatusChip } from '../../../../AirAmbulanceForm/etl/etlStatusUtils';
import styles from './TransportListItem.module.css';
import {
  StyledTransportListItem,
  TransportIconWrapper,
  TransportDetails,
  DetailRow,
} from './TransportListItem.styles';

interface TransportListItemProps {
  transport: EtlTransportResponse;
  isUnread: boolean;
  onClick: () => void;
}

const ARRIVED_STATUS_TYPES: ReadonlySet<string> = new Set([
  'arrivedToScene',
  'arrivedToDropoff',
  'complete',
]);

// `pendingCancellation` renders as "CANCEL ACCEPTED" — a transitional state
// where the cancel is acknowledged but not yet finalized. Other cancel types
// (cancelled, cancelCompleted, cancelPending, cancelDisregarded) are routine
// and remain neutral.
const WARNING_STATUS_TYPES: ReadonlySet<string> = new Set([
  'pendingCancellation',
]);

function getStatusBadgeClassName(type: string | undefined): string {
  if (type === 'error') {
    return `${styles.statusBadge} ${styles.statusBadgeError}`;
  }
  if (type && ARRIVED_STATUS_TYPES.has(type)) {
    return `${styles.statusBadge} ${styles.statusBadgeSuccess}`;
  }
  if (type && WARNING_STATUS_TYPES.has(type)) {
    return `${styles.statusBadge} ${styles.statusBadgeWarning}`;
  }
  return styles.statusBadge;
}

function TransportListItem({
  transport,
  isUnread,
  onClick,
}: TransportListItemProps) {
  const eta = transport.computed_fields.eta;
  const pickup = transport.computed_fields.pickup;
  const source = transport.computed_fields.source;
  const statusChip = resolveTransportStatusChip(transport);
  const createdAtDisplay = formatDateWithLocalTimezone(
    transport.created_at,
    'HH:mm',
  );

  return (
    <StyledTransportListItem onClick={onClick} data-name="transport-list-item">
      <TransportIconWrapper>
        <HelicopterIcon color="primaryText" height={20} width={20} />
      </TransportIconWrapper>

      <TransportDetails>
        <OriginSourceTitleRow>
          <Text
            color="primaryText"
            size="large"
            fontWeight="bold"
            lineHeight="1.2"
          >
            Air Ambulance
          </Text>
          {createdAtDisplay && (
            <span className={styles.createdAtContainer}>
              <span
                className={styles.timestamp}
                data-testid="transport-created-at"
              >
                {createdAtDisplay}
              </span>
              {isUnread && (
                <UnreadNotificationIcon
                  data-testid="transport-list-item-unread-icon"
                  position="center"
                />
              )}
            </span>
          )}
        </OriginSourceTitleRow>

        <div className={styles.statusRow}>
          {statusChip && (
            <span
              data-testid="transport-status-chip"
              className={getStatusBadgeClassName(statusChip.statusType)}
            >
              {statusChip.display}
            </span>
          )}
          <OriginSourcePill
            data-testid="transport-origin"
            className={styles.statusRowSource}
          >
            {source}
          </OriginSourcePill>
        </div>

        {statusChip?.statusType !== 'error' && eta && (
          <DetailRow>
            <ClockIcon color="secondaryText" height={12} width={12} />
            <Text color="primaryText" size="small">
              {eta}
            </Text>
          </DetailRow>
        )}

        {pickup && (
          <DetailRow>
            <MarkerIcon color="secondaryText" height={12} width={12} />
            <Text color="primaryText" size="small">
              {pickup}
            </Text>
          </DetailRow>
        )}

        <DetailRow>
          <PhoneIcon color="secondaryText" height={12} width={12} />
          <Text color="secondaryText" size="small">
            {transport.caller_id
              ? formatInternationalPhoneNumberHuman(transport.caller_id)
              : '—'}
          </Text>
        </DetailRow>
      </TransportDetails>
    </StyledTransportListItem>
  );
}

export default TransportListItem;
